import React, { useState, useEffect, useRef } from "react";
import {
  Card, Row, Col, Button, Form, Table,
  Badge, Alert, ProgressBar, Modal
} from "react-bootstrap";
import Content from "../../../Components/Dashboard/Content/Content";
import DeltaExchangeService from "../../../Service/delta-exchange.service";
import BacktestResultsDisplay from "./BacktestResultsDisplay";
import StrategyDocumentationModal from "./StrategyDocumentationModal";
import BacktestingChart from "./BacktestingChart";
import { symbols, strategies, STRATEGY_PRESETS } from "./BacktestingConfig";
import {
  getIntervalMinutes,
  calculateEMA, calculateRSI, runSeasonalityAnalysis,
  generateMockHistoricalData, prepareChartData,
  calculateMaxConsecutiveWins, calculateMaxConsecutiveLosses
} from "./BacktestingUtils";
import { simulateBacktest, simulateAnimatedBacktest, simulateFastBacktest } from "./BacktestingEngine";
import { createChart } from "lightweight-charts";
import {
  BarChart3, TrendingUp, Calculator, Play, PlayCircle,
  RotateCcw, Download, Settings, Target, DollarSign,
  Shield, Activity, Calendar, Clock, Zap, MonitorPlay
} from "lucide-react";


const Backtesting = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [backtestResults, setBacktestResults] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("BTCUSD");
  const [timeframe, setTimeframe] = useState("1h");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [initialCapital, setInitialCapital] = useState(10000);
  const [riskPerTrade, setRiskPerTrade] = useState(2);
  const [showSettings, setShowSettings] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [progress, setProgress] = useState(0);
  const [chartData, setChartData] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [showCandlestickChart, setShowCandlestickChart] = useState(false);
  const [isBacktestRunning, setIsBacktestRunning] = useState(false);
  const [currentBacktestIndex, setCurrentBacktestIndex] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(20); // ms delay per candle

  const sellMarkersRef = useRef([]);

  // Chart refs
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const buyMarkersRef = useRef([]);
  const [advancedSettings, setAdvancedSettings] = useState({
    stopLoss: 2,
    takeProfit: 4,
    commission: 0.1,
    slippage: 0.05,
    maxTrades: 1000,
    leverage: 1,
    strategyType: "both",
    allowLongTrading: true,
    allowShortSelling: true,
    ema_crossover: {
      fastEMA: 9,
      slowEMA: 21,
      confirmCandles: 1,
    },
  });

  const [patternAnalysis, setPatternAnalysis] = useState(null);
  const [isOptimizationMode, setIsOptimizationMode] = useState(false);
  const [optParams, setOptParams] = useState({
    fastMin: 5, fastMax: 15, fastStep: 1,
    slowMin: 20, slowMax: 40, slowStep: 2
  });
  const [optResults, setOptResults] = useState([]);
  const [isOptimizing, setIsOptimizing] = useState(false);


  const handleStrategyChange = (strategyId) => {
    setSelectedStrategy(strategyId);
    if (strategyId === "ema_crossover") {
      setTimeframe("1h");
      setAdvancedSettings(prev => ({
        ...prev,
        stopLoss: 2,
        takeProfit: 4,
        ema_crossover: { fastEMA: 9, slowEMA: 21, confirmCandles: 1 },
      }));
      setShowSettings(true);
    }
  };


  // Helper function to calculate EMA (Exponential Moving Average)
  // Helper function to calculate RSI (Relative Strength Index) using Wilder's smoothing
  // Render strategy-specific parameters
  const renderStrategyParameters = (strategyId) => {
    const isEMAStrategy = strategyId === "ema_crossover" || strategyId === "ma_crossover" || strategyId === "scalping";
    if (!isEMAStrategy) return null;

    return (
      <div className="p-3 border rounded bg-white mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 fw-bold">Strategy Parameters</h6>
          <Form.Check
            type="switch"
            id="optimization-mode"
            label={<span className="small fw-bold">Enable Logic Optimizer (Range Mode)</span>}
            checked={isOptimizationMode}
            onChange={(e) => setIsOptimizationMode(e.target.checked)}
          />
        </div>

        {isOptimizationMode ? (
          <Row className="g-3">
            <Col md={12}>
              <p className="small fw-bold text-muted mb-2 border-bottom pb-1">FAST EMA RANGE</p>
              <Row className="g-2">
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label className="xx-small fw-bold">Min Period</Form.Label>
                    <Form.Control size="sm" type="number"
                      value={optParams.fastMin}
                      onChange={(e) => setOptParams({ ...optParams, fastMin: parseInt(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label className="xx-small fw-bold">Max Period</Form.Label>
                    <Form.Control size="sm" type="number"
                      value={optParams.fastMax}
                      onChange={(e) => setOptParams({ ...optParams, fastMax: parseInt(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label className="xx-small fw-bold">Step Size</Form.Label>
                    <Form.Control size="sm" type="number"
                      value={optParams.fastStep}
                      onChange={(e) => setOptParams({ ...optParams, fastStep: parseInt(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            <Col md={12} className="mt-3">
              <p className="small fw-bold text-muted mb-2 border-bottom pb-1">SLOW EMA RANGE</p>
              <Row className="g-2">
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label className="xx-small fw-bold">Min Period</Form.Label>
                    <Form.Control size="sm" type="number"
                      value={optParams.slowMin}
                      onChange={(e) => setOptParams({ ...optParams, slowMin: parseInt(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label className="xx-small fw-bold">Max Period</Form.Label>
                    <Form.Control size="sm" type="number"
                      value={optParams.slowMax}
                      onChange={(e) => setOptParams({ ...optParams, slowMax: parseInt(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label className="xx-small fw-bold">Step Size</Form.Label>
                    <Form.Control size="sm" type="number"
                      value={optParams.slowStep}
                      onChange={(e) => setOptParams({ ...optParams, slowStep: parseInt(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
        ) : (
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-bold">Fast EMA Period</Form.Label>
                <Form.Control size="sm" type="number"
                  value={advancedSettings.ma_crossover?.fastEMA || 9}
                  onChange={(e) => setAdvancedSettings(prev => ({
                    ...prev,
                    ma_crossover: { ...prev.ma_crossover, fastEMA: parseInt(e.target.value) },
                    ema_crossover: { ...prev.ema_crossover, fastEMA: parseInt(e.target.value) }
                  }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-bold">Slow EMA Period</Form.Label>
                <Form.Control size="sm" type="number"
                  value={advancedSettings.ma_crossover?.slowEMA || 21}
                  onChange={(e) => setAdvancedSettings(prev => ({
                    ...prev,
                    ma_crossover: { ...prev.ma_crossover, slowEMA: parseInt(e.target.value) },
                    ema_crossover: { ...prev.ema_crossover, slowEMA: parseInt(e.target.value) }
                  }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-bold">Confirm Candles</Form.Label>
                <Form.Control size="sm" type="number" min="0" max="10"
                  value={advancedSettings.ma_crossover?.confirmCandles || 1}
                  onChange={(e) => setAdvancedSettings(prev => ({
                    ...prev,
                    ma_crossover: { ...prev.ma_crossover, confirmCandles: parseInt(e.target.value) },
                    ema_crossover: { ...prev.ema_crossover, confirmCandles: parseInt(e.target.value) }
                  }))}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
      </div>
    );
  };



  // Helper function to get default values for parameters
  const getDefaultValue = (strategyId, paramKey) => {
    const defaults = {
      trendFollowing: {
        shortMA: 10,
        longMA: 20,
        trendStrength: 0.5,
        volumeFilter: true,
      },
      meanReversion: {
        lookbackPeriod: 20,
        oversoldThreshold: 30,
        overboughtThreshold: 70,
        rsiPeriod: 14,
        bbPeriod: 20,
        bbStdDev: 2,
      },
      breakout: {
        lookbackPeriod: 20,
        breakoutThreshold: 0.02,
        volumeConfirmation: true,
        minVolumeRatio: 1.5,
      },
      scalping: {
        shortMA: 5,
        longMA: 10,
        profitTarget: 0.5,
        stopLoss: 0.3,
        maxHoldTime: 30,
      },
      momentum: {
        momentumPeriod: 10,
        momentumThreshold: 0.02,
        volumePeriod: 14,
        volumeThreshold: 1.2,
      },
      rsiStrategy: {
        rsiPeriod: 14,
        oversoldLevel: 30,
        overboughtLevel: 70,
        exitLevel: 50,
      },
      bollingerBands: {
        bbPeriod: 20,
        bbStdDev: 2,
        squeezeThreshold: 0.1,
        breakoutThreshold: 0.02,
      },
      macdStrategy: {
        fastPeriod: 12,
        slowPeriod: 26,
        signalPeriod: 9,
        histogramThreshold: 0,
      },
      gridTrading: {
        gridLevels: 10,
        gridSpacing: 0.5,
        maxPositions: 5,
        rebalanceThreshold: 0.1,
      },
      dcaStrategy: {
        dcaAmount: 100,
        dcaInterval: 24,
        maxDcaCount: 10,
        profitTarget: 5,
      },
    };

    return defaults[strategyId]?.[paramKey] || 0;
  };

  // Validation function for strategy parameters
  const validateStrategyParameters = (strategyId) => {
    const currentSettings = advancedSettings[strategyId] || {};
    const errors = [];
    const warnings = [];

    switch (strategyId) {
      case "ma_crossover":
        if (
          currentSettings.fastEMA &&
          currentSettings.slowEMA &&
          currentSettings.fastEMA >= currentSettings.slowEMA
        ) {
          errors.push("Fast EMA period must be less than Slow EMA period");
        }
        break;

      case "rsi_ma_confluence":
        if (
          currentSettings.oversoldLevel &&
          currentSettings.overboughtLevel &&
          currentSettings.oversoldLevel >= currentSettings.overboughtLevel
        ) {
          errors.push(
            "Oversold level must be less than overbought level"
          );
        }
        if (
          currentSettings.oversoldLevel &&
          (currentSettings.oversoldLevel < 0 ||
            currentSettings.oversoldLevel > 50)
        ) {
          warnings.push("Oversold level should be between 0 and 50");
        }
        if (
          currentSettings.overboughtLevel &&
          (currentSettings.overboughtLevel < 50 ||
            currentSettings.overboughtLevel > 100)
        ) {
          warnings.push("Overbought level should be between 50 and 100");
        }
        break;

      case "breakout_strategy":
        if (
          currentSettings.breakoutThreshold &&
          (currentSettings.breakoutThreshold < 0 ||
            currentSettings.breakoutThreshold > 5)
        ) {
          warnings.push("Breakout threshold should be between 0 and 5%");
        }
        break;

      case "price_action":
        if (
          currentSettings.pinBarThreshold &&
          (currentSettings.pinBarThreshold < 0 ||
            currentSettings.pinBarThreshold > 1)
        ) {
          warnings.push("Pin bar threshold should be between 0 and 1");
        }
        break;

      case "macd_reversal":
        if (
          currentSettings.fastPeriod &&
          currentSettings.slowPeriod &&
          currentSettings.fastPeriod >= currentSettings.slowPeriod
        ) {
          errors.push("Fast period must be less than slow period");
        }
        break;
    }

    return { errors, warnings };
  };

  // Save parameter preset
  const saveParameterPreset = (strategyId, presetName) => {
    const currentSettings = advancedSettings[strategyId] || {};
    const presets = JSON.parse(localStorage.getItem("strategyPresets") || "{}");

    if (!presets[strategyId]) {
      presets[strategyId] = {};
    }

    presets[strategyId][presetName] = {
      ...currentSettings,
      savedAt: new Date().toISOString(),
      name: presetName,
    };

    localStorage.setItem("strategyPresets", JSON.stringify(presets));
    alert(`✅ Preset "${presetName}" saved successfully!`);
  };

  // Load parameter preset
  const loadParameterPreset = (strategyId, presetName) => {
    const presets = JSON.parse(localStorage.getItem("strategyPresets") || "{}");
    const preset = presets[strategyId]?.[presetName];

    if (preset) {
      setAdvancedSettings((prev) => ({
        ...prev,
        [strategyId]: preset,
      }));
      alert(`✅ Preset "${presetName}" loaded successfully!`);
    } else {
      alert("❌ Preset not found!");
    }
  };

  // Get available presets for a strategy
  const getAvailablePresets = (strategyId) => {
    const presets = JSON.parse(localStorage.getItem("strategyPresets") || "{}");
    return presets[strategyId] ? Object.keys(presets[strategyId]) : [];
  };

  // Generate strategy signals for test animation
  const generateStrategySignals = (candlestickData) => {
    if (!selectedStrategy || !candlestickData.length) return [];

    const signals = [];
    const prices = candlestickData.map((candle) => candle.close);
    const volumes = candlestickData.map((candle) => candle.volume || 1000); // Default volume if not available
    const strategyParams = advancedSettings[selectedStrategy] || {};

    // Get minimum lookback period based on strategy
    const minLookback = Math.max(
      strategyParams.fastEMA || 50,
      strategyParams.slowEMA || 200,
      strategyParams.rsiPeriod || 14,
      strategyParams.emaPeriod || 20,
      strategyParams.lookbackPeriod || 20,
      strategyParams.lookbackCandles || 20,
      strategyParams.slowPeriod || 26,
      strategyParams.signalPeriod || 9
    );

    // Helper function to calculate EMA (corrected calculation)
    const calculateEMALocal = (prices, index, period) => {
      if (index < period - 1) return null;

      // Initialize EMA with SMA for the first period
      let sum = 0;
      for (let i = index - period + 1; i <= index; i++) {
        sum += prices[i];
      }
      let ema = sum / period;

      // Calculate EMA using the multiplier
      const multiplier = 2 / (period + 1);

      // Continue from the next index if we have more data
      for (let i = index - period + 2; i <= index; i++) {
        ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
      }

      return ema;
    };

    // Helper function to calculate RSI (using Wilder's smoothing)
    const calculateRSILocal = (prices, index, period) => {
      if (index < period) return null;

      let avgGain = 0;
      let avgLoss = 0;

      // Calculate initial average gain and loss
      for (let i = index - period + 1; i <= index; i++) {
        const change = prices[i] - prices[i - 1];
        if (change > 0) {
          avgGain += change;
        } else {
          avgLoss += Math.abs(change);
        }
      }

      avgGain = avgGain / period;
      avgLoss = avgLoss / period;

      // Use Wilder's smoothing for subsequent calculations
      if (index > period) {
        const currentChange = prices[index] - prices[index - 1];
        const currentGain = currentChange > 0 ? currentChange : 0;
        const currentLoss = currentChange < 0 ? Math.abs(currentChange) : 0;

        avgGain = (avgGain * (period - 1) + currentGain) / period;
        avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
      }

      if (avgLoss === 0) return 100;

      const rs = avgGain / avgLoss;
      return 100 - (100 / (1 + rs));
    };

    // Strategy signal generation functions for the 5 new strategies

    // 1. Moving Average Crossover Strategy (50 EMA + 200 EMA)
    const getMACrossoverSignal = (index, params) => {
      const fastEMA = params.fastEMA || 50;
      const slowEMA = params.slowEMA || 200;

      if (index < slowEMA) return "HOLD";

      const fastEmaValue = calculateEMALocal(prices, index, fastEMA);
      const slowEmaValue = calculateEMALocal(prices, index, slowEMA);

      if (index < slowEMA + 1) return "HOLD";
      const prevFastEma = calculateEMALocal(prices, index - 1, fastEMA);
      const prevSlowEma = calculateEMALocal(prices, index - 1, slowEMA);

      if (fastEmaValue > slowEmaValue && prevFastEma <= prevSlowEma) {
        return "BUY";
      }
      if (fastEmaValue < slowEmaValue && prevFastEma >= prevSlowEma) {
        return "SELL";
      }
      return "HOLD";
    };

    // 2. RSI + MA Confluence Strategy (RSI 14 + 20 EMA)
    const getRSIMAConfluenceSignal = (index, params) => {
      const rsiPeriod = params.rsiPeriod || 14;
      const emaPeriod = params.emaPeriod || 20;
      const oversoldLevel = params.oversoldLevel || 30;
      const overboughtLevel = params.overboughtLevel || 70;

      if (index < Math.max(rsiPeriod, emaPeriod) + 1) return "HOLD";

      const rsi = calculateRSILocal(prices, index, rsiPeriod);
      const ema = calculateEMALocal(prices, index, emaPeriod);
      const currentPrice = prices[index];

      if (rsi < oversoldLevel && currentPrice > ema) {
        return "BUY";
      }
      if (rsi > overboughtLevel && currentPrice < ema) {
        return "SELL";
      }
      return "HOLD";
    };

    // 3. Breakout Strategy (London/Asian Breakout)
    const getBreakoutStrategySignal = (index, params) => {
      const lookbackPeriod = params.lookbackPeriod || 20;
      const breakoutThreshold = (params.breakoutThreshold || 0.5) / 100;
      const sessionType = params.sessionType || "london";

      if (index < lookbackPeriod) return "HOLD";

      // Get timestamp from candlestick data if available
      const currentTime = candlestickData[index]?.time ? new Date(candlestickData[index].time * 1000) : new Date();
      const hour = currentTime.getUTCHours();

      let inSession = false;
      if (sessionType === "london") {
        inSession = hour >= 8 && hour < 17;
      } else if (sessionType === "asian") {
        inSession = hour >= 0 && hour < 9;
      } else if (sessionType === "both") {
        inSession = (hour >= 8 && hour < 17) || (hour >= 0 && hour < 9);
      }

      if (!inSession) return "HOLD";

      const recentPrices = prices.slice(index - lookbackPeriod, index);
      const resistance = Math.max(...recentPrices);
      const support = Math.min(...recentPrices);
      const currentPrice = prices[index];

      if (currentPrice > resistance * (1 + breakoutThreshold)) {
        return "BUY";
      }
      if (currentPrice < support * (1 - breakoutThreshold)) {
        return "SELL";
      }
      return "HOLD";
    };

    // 4. Price Action Strategy (Pin Bar, Engulfing, Break & Retest)
    const getPriceActionSignal = (index, params) => {
      const pinBarThreshold = params.pinBarThreshold || 0.6;
      const engulfingSize = (params.engulfingSize || 0.3) / 100;
      const retestTolerance = (params.retestTolerance || 0.2) / 100;
      const lookbackCandles = params.lookbackCandles || 20;

      if (index < lookbackCandles + 2 || !candlestickData[index]) return "HOLD";

      const currentCandle = candlestickData[index];
      const prevCandle = candlestickData[index - 1];

      const open = parseFloat(currentCandle.open);
      const high = parseFloat(currentCandle.high);
      const low = parseFloat(currentCandle.low);
      const close = parseFloat(currentCandle.close);

      const prevOpen = parseFloat(prevCandle.open);
      const prevHigh = parseFloat(prevCandle.high);
      const prevLow = parseFloat(prevCandle.low);
      const prevClose = parseFloat(prevCandle.close);

      const bodySize = Math.abs(close - open);
      const candleRange = high - low;
      const upperWick = high - Math.max(open, close);
      const lowerWick = Math.min(open, close) - low;

      // Pin Bar detection
      const isBullishPin = candleRange > 0 && lowerWick / candleRange > pinBarThreshold &&
        close > open &&
        upperWick / candleRange < 0.3;
      const isBearishPin = candleRange > 0 && upperWick / candleRange > pinBarThreshold &&
        close < open &&
        lowerWick / candleRange < 0.3;

      // Engulfing pattern detection
      const isBullishEngulfing = prevClose < prevOpen &&
        close > open &&
        open < prevClose &&
        close > prevOpen &&
        bodySize > Math.abs(prevClose - prevOpen) * (1 + engulfingSize);
      const isBearishEngulfing = prevClose > prevOpen &&
        close < open &&
        open > prevClose &&
        close < prevOpen &&
        bodySize > Math.abs(prevOpen - prevClose) * (1 + engulfingSize);

      // Break & Retest detection
      const lookbackPrices = prices.slice(Math.max(0, index - lookbackCandles), index);
      if (lookbackPrices.length > 0) {
        const support = Math.min(...lookbackPrices);
        const resistance = Math.max(...lookbackPrices);
        const currentPrice = close;

        const isBreakAboveRetest = currentPrice > resistance * (1 - retestTolerance) &&
          currentPrice < resistance * (1 + retestTolerance) &&
          low <= resistance;
        const isBreakBelowRetest = currentPrice < support * (1 + retestTolerance) &&
          currentPrice > support * (1 - retestTolerance) &&
          high >= support;

        if (isBullishPin || isBullishEngulfing || isBreakAboveRetest) {
          return "BUY";
        }
        if (isBearishPin || isBearishEngulfing || isBreakBelowRetest) {
          return "SELL";
        }
      }

      return "HOLD";
    };

    // 5. MACD Trend Reversal Strategy
    const getMACDReversalSignal = (index, params) => {
      const fastPeriod = params.fastPeriod || 12;
      const slowPeriod = params.slowPeriod || 26;
      const signalPeriod = params.signalPeriod || 9;
      const histogramThreshold = params.histogramThreshold || 0;

      if (index < slowPeriod + signalPeriod) return "HOLD";

      const fastEMA = calculateEMALocal(prices, index, fastPeriod);
      const slowEMA = calculateEMALocal(prices, index, slowPeriod);
      const macd = fastEMA - slowEMA;

      // Calculate signal line (EMA of MACD)
      const macdValues = [];
      for (let i = index - signalPeriod; i <= index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        macdValues.push(fEMA - sEMA);
      }
      const signalLine = macdValues.reduce((a, b) => a + b) / macdValues.length;

      const histogram = macd - signalLine;

      if (index < slowPeriod + signalPeriod + 1) return "HOLD";

      const prevFastEMA = calculateEMALocal(prices, index - 1, fastPeriod);
      const prevSlowEMA = calculateEMALocal(prices, index - 1, slowPeriod);
      const prevMACD = prevFastEMA - prevSlowEMA;

      const prevMacdValues = [];
      for (let i = index - signalPeriod - 1; i < index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        prevMacdValues.push(fEMA - sEMA);
      }
      const prevSignalLine = prevMacdValues.reduce((a, b) => a + b) / prevMacdValues.length;
      const prevHistogram = prevMACD - prevSignalLine;

      if (macd > signalLine && prevMACD <= prevSignalLine && histogram > histogramThreshold) {
        return "BUY";
      }
      if (macd < signalLine && prevMACD >= prevSignalLine && histogram < -histogramThreshold) {
        return "SELL";
      }
      return "HOLD";
    };

    // Generate signals for each candle
    for (let i = minLookback; i < candlestickData.length; i++) {
      let signal = "HOLD";

      switch (selectedStrategy) {
        case "ma_crossover":
          signal = getMACrossoverSignal(i, strategyParams);
          break;
        case "rsi_ma_confluence":
          signal = getRSIMAConfluenceSignal(i, strategyParams);
          break;
        case "breakout_strategy":
          signal = getBreakoutStrategySignal(i, strategyParams);
          break;
        case "price_action":
          signal = getPriceActionSignal(i, strategyParams);
          break;
        case "macd_reversal":
          signal = getMACDReversalSignal(i, strategyParams);
          break;
        default:
          signal = "HOLD";
      }

      if (signal !== "HOLD") {
        signals.push({
          time: candlestickData[i].time,
          price: prices[i],
          signal: signal,
          candleIndex: i,
          timestamp: new Date(candlestickData[i].time * 1000).toISOString(),
        });
      }
    }

    return signals;
  };

  // Test animation with strategy signals
  const runTestAnimationWithSignals = async () => {
    if (
      !candleSeriesRef.current ||
      !historicalData.length ||
      !selectedStrategy
    ) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    const testData = historicalData
      .map((candle) => ({
        time: Math.floor(new Date(candle.timestamp).getTime() / 1000),
        open: parseFloat(candle.open),
        high: parseFloat(candle.high),
        low: parseFloat(candle.low),
        close: parseFloat(candle.close),
      }))
      .sort((a, b) => a.time - b.time);

    // Generate strategy signals
    const signals = generateStrategySignals(testData);

    // Clear chart first
    candleSeriesRef.current.setData([]);
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    // Animate with signals
    for (let i = 0; i < testData.length; i++) {
      const currentData = testData.slice(0, i + 1);
      candleSeriesRef.current.setData(currentData);

      // Add signals that should appear at current candle
      const currentSignals = signals.filter(
        (signal) => signal.candleIndex === i
      );

      currentSignals.forEach((signal) => {
        try {
          if (signal.signal === "BUY") {
            const marker = candleSeriesRef.current.createPriceLine({
              price: signal.price,
              color: "#26a69a",
              lineWidth: 3,
              lineStyle: 0,
              axisLabelVisible: true,
              title: `BUY ${signal.price.toFixed(2)}`,
            });
            buyMarkersRef.current.push(marker);
          } else if (signal.signal === "SELL") {
            const marker = candleSeriesRef.current.createPriceLine({
              price: signal.price,
              color: "#ef5350",
              lineWidth: 3,
              lineStyle: 0,
              axisLabelVisible: true,
              title: `SELL ${signal.price.toFixed(2)}`,
            });
            sellMarkersRef.current.push(marker);
          }
        } catch (error) {
          console.error("Error creating signal marker:", error);
        }
      });

      // Update progress
      setCurrentBacktestIndex(i);
      setProgress((i / testData.length) * 100);

      await new Promise((resolve) => setTimeout(resolve, 30)); // 30ms delay
    }

    setProgress(100);
  };

  // Show all strategy signals at once (no animation)
  const showAllStrategySignals = () => {
    if (
      !candleSeriesRef.current ||
      !historicalData.length ||
      !selectedStrategy
    ) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    const testData = historicalData
      .map((candle) => ({
        time: Math.floor(new Date(candle.timestamp).getTime() / 1000),
        open: parseFloat(candle.open),
        high: parseFloat(candle.high),
        low: parseFloat(candle.low),
        close: parseFloat(candle.close),
      }))
      .sort((a, b) => a.time - b.time);

    // Generate strategy signals
    const signals = generateStrategySignals(testData);

    // Show all data at once
    candleSeriesRef.current.setData(testData);

    // Clear existing markers
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    // Add all signals
    signals.forEach((signal) => {
      try {
        if (signal.signal === "BUY") {
          const marker = candleSeriesRef.current.createPriceLine({
            price: signal.price,
            color: "#26a69a",
            lineWidth: 3,
            lineStyle: 0,
            axisLabelVisible: true,
            title: `BUY ${signal.price.toFixed(2)}`,
          });
          buyMarkersRef.current.push(marker);
        } else if (signal.signal === "SELL") {
          const marker = candleSeriesRef.current.createPriceLine({
            price: signal.price,
            color: "#ef5350",
            lineWidth: 3,
            lineStyle: 0,
            axisLabelVisible: true,
            title: `SELL ${signal.price.toFixed(2)}`,
          });
          sellMarkersRef.current.push(marker);
        }
      } catch (error) {
        console.error("Error creating signal marker:", error);
      }
    });

    // Update progress
    setCurrentBacktestIndex(testData.length - 1);
    setProgress(100);
  };

  // Render strategy documentation
  // Available Symbols for Backtesting


  // Set default dates
  useEffect(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    setEndDate(today.toISOString().split("T")[0]);
    setStartDate(oneMonthAgo.toISOString().split("T")[0]);
  }, []);

  // Initialize candlestick chart when historical data changes
  useEffect(() => {
    if (showCandlestickChart && historicalData.length > 0) {
      initializeCandlestickChart();
    }
  }, [showCandlestickChart, historicalData]);

  // Initialize candlestick chart (empty for animation)
  const initializeCandlestickChart = () => {
    if (!chartContainerRef.current) {
      return;
    }

    try {
      // Clean up existing chart
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
      }

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 600,
        layout: {
          background: { color: "#ffffff" },
          textColor: "#333",
        },
        grid: {
          vertLines: { color: "#f0f0f0" },
          horzLines: { color: "#f0f0f0" },
        },
        crosshair: {
          mode: 1,
        },
        rightPriceScale: {
          borderColor: "#cccccc",
        },
        timeScale: {
          borderColor: "#cccccc",
          timeVisible: true,
          secondsVisible: false,
        },
      });

      const candleSeries = chart.addCandlestickSeries({
        upColor: "#26a69a",
        downColor: "#ef5350",
        borderUpColor: "#26a69a",
        borderDownColor: "#ef5350",
        wickUpColor: "#26a69a",
        wickDownColor: "#ef5350",
      });

      // Start with empty data for animation
      candleSeries.setData([]);

      chartRef.current = chart;
      candleSeriesRef.current = candleSeries;

      // Handle resize
      const handleResize = () => {
        if (chart && chartContainerRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        chart.remove();
      };
    } catch (error) {
      console.error("Error initializing candlestick chart:", error);
      alert("Error initializing chart: " + error.message);
    }
  };

  // Animate candlestick data progressively
  const animateCandlestickData = async (candlestickData, trades = []) => {
    if (!candleSeriesRef.current) {
      console.error("CandleSeriesRef is null!");
      return;
    }

    if (!candlestickData.length) {
      console.error("No candlestick data provided!");
      return;
    }

    // Clear existing data and markers
    candleSeriesRef.current.setData([]);
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    // Animate candles progressively - ONE CANDLE AT A TIME
    for (let i = 0; i < candlestickData.length; i++) {
      // Only show current candle + previous candles (progressive build-up)
      const currentData = candlestickData.slice(0, i + 1);

      try {
        // Set data with only current candles
        candleSeriesRef.current.setData(currentData);

        // Update progress
        setCurrentBacktestIndex(i);
        setProgress((i / candlestickData.length) * 100);

        // Add trade markers for current trades only
        const currentTrades = trades.filter((trade) => {
          const tradeTime = Math.floor(
            new Date(trade.timestamp).getTime() / 1000
          );
          return tradeTime <= candlestickData[i].time;
        });

        // Update trade markers
        if (currentTrades.length > 0) {
          // Clear existing markers
          buyMarkersRef.current.forEach((marker) =>
            candleSeriesRef.current.removePriceLine(marker)
          );
          sellMarkersRef.current.forEach((marker) =>
            candleSeriesRef.current.removePriceLine(marker)
          );
          buyMarkersRef.current = [];
          sellMarkersRef.current = [];

          // Add new markers
          currentTrades.forEach((trade) => {
            try {
              if (trade.type === "OPEN_LONG" || trade.type === "CLOSE_SHORT") {
                const marker = candleSeriesRef.current.createPriceLine({
                  price: trade.price,
                  color: "#26a69a",
                  lineWidth: 2,
                  lineStyle: 0,
                  axisLabelVisible: true,
                  title: `BUY ${trade.price.toFixed(2)}`,
                });
                buyMarkersRef.current.push(marker);
              } else if (
                trade.type === "OPEN_SHORT" ||
                trade.type === "CLOSE_LONG"
              ) {
                const marker = candleSeriesRef.current.createPriceLine({
                  price: trade.price,
                  color: "#ef5350",
                  lineWidth: 2,
                  lineStyle: 0,
                  axisLabelVisible: true,
                  title: `SELL ${trade.price.toFixed(2)}`,
                });
                sellMarkersRef.current.push(marker);
              }
            } catch (error) {
              console.error("Error creating trade marker:", error);
            }
          });
        }

        // Animation delay - adjustable speed
        await new Promise((resolve) => setTimeout(resolve, animationSpeed));

        // Break if backtest is stopped
        if (!isBacktestRunning) {
          break;
        }
      } catch (error) {
        console.error(`Error setting data at candle ${i}:`, error);
        break;
      }
    }
  };

  // Add trade markers to chart
  const addTradeMarkers = (trades) => {
    if (!candleSeriesRef.current || !trades.length) return;

    // Clear existing markers
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    trades.forEach((trade) => {
      const time = Math.floor(new Date(trade.timestamp).getTime() / 1000);

      if (trade.type === "OPEN_LONG" || trade.type === "CLOSE_SHORT") {
        const marker = candleSeriesRef.current.createPriceLine({
          price: trade.price,
          color: "#26a69a",
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: `BUY ${trade.price.toFixed(2)}`,
        });
        buyMarkersRef.current.push(marker);
      } else if (trade.type === "OPEN_SHORT" || trade.type === "CLOSE_LONG") {
        const marker = candleSeriesRef.current.createPriceLine({
          price: trade.price,
          color: "#ef5350",
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: `SELL ${trade.price.toFixed(2)}`,
        });
        sellMarkersRef.current.push(marker);
      }
    });
  };

  // Animated backtest execution
  const runAnimatedBacktest = async () => {
    if (!selectedStrategy || !historicalData.length) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    setIsBacktestRunning(true);
    setCurrentBacktestIndex(0);
    setProgress(0);

    try {
      // Initialize empty candlestick chart
      initializeCandlestickChart();
      setShowCandlestickChart(true);

      // Prepare candlestick data
      const candlestickData = historicalData
        .map((candle) => {
          const timestamp = new Date(candle.timestamp);
          if (isNaN(timestamp.getTime())) {
            console.warn("Invalid timestamp:", candle.timestamp);
            return null;
          }

          return {
            time: Math.floor(timestamp.getTime() / 1000),
            open: parseFloat(candle.open) || 0,
            high: parseFloat(candle.high) || 0,
            low: parseFloat(candle.low) || 0,
            close: parseFloat(candle.close) || 0,
          };
        })
        .filter((item) => item !== null)
        .sort((a, b) => a.time - b.time);

      // Run backtest simulation
      const context = { historicalData, initialCapital, advancedSettings, selectedStrategy, selectedSymbol, timeframe, startDate, endDate, setProgress, setCurrentBacktestIndex, setPatternAnalysis, buyMarkersRef, sellMarkersRef, candleSeriesRef, riskPerTrade };
      const results = await simulateAnimatedBacktest(context);
      console.log("results", results)
      setBacktestResults(results);

      // Start candlestick animation with trades
      await animateCandlestickData(candlestickData, results.trades);

      setProgress(100);
    } catch (error) {
      console.error("Animated backtest error:", error);
    }

    setIsLoading(false);
  };

  // Strategy Optimization Logic
  const runOptimization = async () => {
    if (!selectedStrategy || !historicalData.length) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    setIsOptimizing(true);
    setOptResults([]);

    // Simulate short progress
    setProgress(10);

    const results = [];
    const { fastMin, fastMax, fastStep, slowMin, slowMax, slowStep } = optParams;

    // We only support EMA Crossover optimization for now
    if (selectedStrategy !== "ema_crossover" && selectedStrategy !== "scalping") {
      alert("Optimization is currently only available for EMA-based strategies.");
      setIsOptimizing(false);
      return;
    }

    try {
      for (let f = parseInt(fastMin); f <= parseInt(fastMax); f += parseInt(fastStep)) {
        for (let s = parseInt(slowMin); s <= parseInt(slowMax); s += parseInt(slowStep)) {
          if (f >= s) continue; // Invalid crossover

          const tempSettings = {
            ...advancedSettings,
            ema_crossover: { ...advancedSettings.ema_crossover, fastEMA: f, slowEMA: s },
            ma_crossover: { ...advancedSettings.ma_crossover, fastEMA: f, slowEMA: s }
          };

          const result = simulateFastBacktest({
            historicalData,
            initialCapital,
            advancedSettings: tempSettings,
            riskPerTrade,
            leverage: advancedSettings.leverage
          });

          results.push({
            fastEMA: f,
            slowEMA: s,
            ...result
          });
        }
        // Update progress occasionally
        setProgress(Math.min(90, 10 + (f / fastMax) * 80));
        await new Promise(r => setTimeout(r, 0)); // Allow UI to breathe
      }

      // Sort by Net Profit
      results.sort((a, b) => b.netProfit - a.netProfit);
      setOptResults(results);
      setProgress(100);
      alert(`Optimization complete! Tested ${results.length} combinations.`);
    } catch (error) {
      console.error("Optimization error:", error);
    }

    setIsOptimizing(false);
  };

  const applyOptResult = (result) => {
    setAdvancedSettings(prev => ({
      ...prev,
      ema_crossover: { ...prev.ema_crossover, fastEMA: result.fastEMA, slowEMA: result.slowEMA },
      ma_crossover: { ...prev.ma_crossover, fastEMA: result.fastEMA, slowEMA: result.slowEMA }
    }));
    setShowSettings(true);
    alert(`Applied parameters: Fast EMA ${result.fastEMA}, Slow EMA ${result.slowEMA}. Now you can run a detailed simulation.`);
  };

  // Helper function to get default values for parameters
  const fetchHistoricalData = async () => {
    setIsLoading(true);
    setProgress(0);

    try {
      // Validate parameters
      const validation = DeltaExchangeService.validateBacktestParams({
        symbol: selectedSymbol,
        timeframe: timeframe,
        startDate: startDate,
        endDate: endDate,
        strategy: selectedStrategy,
        initialCapital: initialCapital,
        riskPerTrade: riskPerTrade,
      });

      if (!validation.isValid) {
        alert(
          "Please fix the following errors:\n" + validation.errors.join("\n")
        );
        setIsLoading(false);
        return;
      }

      // Fetch historical data using service
      const response = await DeltaExchangeService.getHistoricalData({
        symbol: selectedSymbol,
        timeframe: timeframe,
        startDate: startDate,
        endDate: endDate,
      });

      if (response.success) {
        // Sort data by timestamp to ensure proper order
        const sortedData = (response.data.candles || []).sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        setHistoricalData(sortedData);

        // Prepare chart data
        const chartData = prepareChartData(sortedData);
        setChartData(chartData);
        setShowChart(true);

        // Simulate progress
        for (let i = 0; i <= 100; i += 10) {
          setProgress(i);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } else {
        throw new Error(response.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching historical data:", error);
      alert("Error fetching historical data: " + error.message);

      // Use mock data for demonstration
      const mockData = generateMockHistoricalData(startDate, endDate, timeframe);
      setHistoricalData(mockData);

      // Prepare chart data for mock data
      const chartData = prepareChartData(mockData);
      setChartData(chartData);
      setShowChart(true);

      setProgress(100);
    }

    setIsLoading(false);
  };

  // Clear all backtest data
  const clearAllData = () => {
    setBacktestResults(null);
    setHistoricalData([]);
    setChartData(null);
    setShowChart(false);
    setShowCandlestickChart(false);
    setProgress(0);
    setSelectedStrategy("");
    setStartDate("");
    setEndDate("");
    setCurrentBacktestIndex(0);

    // Clear localStorage
    localStorage.removeItem("strategyPresets");

    // Clear chart markers
    if (buyMarkersRef.current) buyMarkersRef.current = [];
    if (sellMarkersRef.current) sellMarkersRef.current = [];

    // Clear chart data if chart exists
    if (candleSeriesRef.current) {
      candleSeriesRef.current.setData([]);
    }

    alert("All backtest data has been cleared!");
  };

  const runBacktest = async () => {

    if (!selectedStrategy || !historicalData.length) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    setIsLoading(true);
    setProgress(0);

    try {
      // Simulate backtesting process
      const context = { historicalData, initialCapital, advancedSettings, selectedStrategy, selectedSymbol, timeframe, startDate, endDate, setPatternAnalysis, riskPerTrade };
      console.log("context ", context)
      const results = await simulateBacktest(context);
      setBacktestResults(results);

      // Trigger Seasonality Analysis explicitly
      if (results && results.trades) {
        const seasonalityData = runSeasonalityAnalysis(results.trades);
        setPatternAnalysis(seasonalityData);
      }

      // Update chart with trade markers
      const updatedChartData = prepareChartData(historicalData, results.trades);
      setChartData(updatedChartData);

      // Simulate progress
      for (let i = 0; i <= 100; i += 5) {
        setProgress(i);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    } catch (error) {
      console.error("Backtest error:", error);
    }

    setIsLoading(false);
  };

  // Generate detailed trade-by-trade report
  const generateTradeReport = () => {
    if (!backtestResults || !backtestResults.trades) return [];

    const tradeReport = [];
    const trades = backtestResults.trades;

    let currentTrade = null;

    for (let i = 0; i < trades.length; i++) {
      const trade = trades[i];

      if (trade.type === "OPEN_LONG" || trade.type === "OPEN_SHORT") {
        currentTrade = {
          tradeNumber: tradeReport.length + 1,
          type: trade.type === "OPEN_LONG" ? "LONG" : "SHORT",
          entryTime: new Date(trade.timestamp).toLocaleString(),
          entryTimestamp: trade.timestamp, // Store original timestamp for duration calculation
          entryPrice: trade.price.toFixed(2),
          quantity: trade.amount || 0,
          fastEMA: trade.indicatorValues?.fastEMA?.toFixed(2) || "N/A",
          slowEMA: trade.indicatorValues?.slowEMA?.toFixed(2) || "N/A",
          exitTime: "",
          exitPrice: "",
          profit: 0,
          profitPercent: 0,
          exitReason: "",
          duration: "",
        };
      } else if (trade.type === "CLOSE_LONG" || trade.type === "CLOSE_SHORT") {
        if (currentTrade) {
          const entryPrice = parseFloat(currentTrade.entryPrice);
          const exitPrice = trade.price;
          const quantity = currentTrade.quantity;

          // Use profit from trade object (already calculated with commissions/slippage)
          const profit = trade.profit || 0;
          const profitPercent = quantity > 0 && entryPrice > 0
            ? (profit / (entryPrice * quantity)) * 100
            : 0;

          // Calculate duration using original timestamps
          const entryTime = new Date(currentTrade.entryTimestamp);
          const exitTime = new Date(trade.timestamp);
          const durationMs = exitTime.getTime() - entryTime.getTime();

          // Handle invalid dates
          let durationStr = "N/A";
          if (!isNaN(durationMs) && durationMs >= 0) {
            const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
            const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
            durationStr = `${durationHours}h ${durationMinutes}m`;
          }

          currentTrade.exitTime = exitTime.toLocaleString();
          currentTrade.exitPrice = exitPrice.toFixed(2);
          currentTrade.profit = profit;
          currentTrade.profitPercent = profitPercent;
          currentTrade.exitReason = trade.reason || "SIGNAL_CLOSE";
          currentTrade.exitFastEMA = trade.indicatorValues?.fastEMA?.toFixed(2) || "N/A";
          currentTrade.exitSlowEMA = trade.indicatorValues?.slowEMA?.toFixed(2) || "N/A";
          currentTrade.entryCandle = trades.find(t => t.timestamp === currentTrade.entryTimestamp)?.candle;
          currentTrade.exitCandle = trade.candle;
          currentTrade.duration = durationStr;

          tradeReport.push(currentTrade);
          currentTrade = null;
        }
      }
    }

    return tradeReport;
  };

  const exportResults = () => {
    if (!backtestResults) return;

    const tradeReport = generateTradeReport();

    const csvContent = [
      ["Strategy", backtestResults.strategy],
      ["Symbol", backtestResults.symbol],
      ["Timeframe", backtestResults.timeframe],
      ["Period", backtestResults.period],
      ["Initial Capital", backtestResults.initialCapital],
      ["Final Capital", backtestResults.finalCapital],
      ["Total Return (%)", backtestResults.totalReturn.toFixed(2)],
      ["Total Trades", backtestResults.totalTrades],
      ["Winning Trades", backtestResults.winningTrades],
      ["Win Rate (%)", backtestResults.winRate.toFixed(2)],
      ["Max Drawdown (%)", backtestResults.maxDrawdown.toFixed(2)],
      ["", ""],
      ["Trade Report", ""],
      ["Trade #", "Type", "Entry Time", "Entry Price", "Exit Time", "Exit Price", "Profit", "Profit %", "Exit Reason", "Duration"],
      ...tradeReport.map(t => [
        t.tradeNumber,
        t.type,
        t.entryTime,
        t.entryPrice,
        t.exitTime,
        t.exitPrice,
        t.profit.toFixed(2),
        t.profitPercent.toFixed(2),
        t.exitReason,
        t.duration
      ])
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `backtest-results-${selectedSymbol}-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Content
      Page_title="Backtesting & Strategy Simulation"
      button_status={false}
    >
      <div className="backtesting-container">
        {/* Configuration Section - Step 1 & 2 */}
        <Row className="mb-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
              <Card.Header className="bg-primary bg-gradient d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0 d-flex align-items-center text-white">
                  <Settings size={20} className="me-2" />
                  Step 1 & 2: Strategy Configuration
                </h5>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setShowSettings(!showSettings)}
                  className="rounded-pill px-3"
                >
                  {showSettings ? "Hide" : "Show"} Advanced Options
                </Button>
              </Card.Header>
              <Card.Body className="p-4">
                {/* Basic Settings - Always Visible */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold text-dark d-flex align-items-center">
                        <Target size={16} className="me-2 text-primary" />
                        Select Trading Strategy
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Select
                          value={selectedStrategy}
                          onChange={(e) => handleStrategyChange(e.target.value)}
                          className="shadow-sm border-2 rounded-3 py-2"
                        >
                          <option value="">-- Choose Strategy --</option>
                          {strategies.map((strategy) => (
                            <option key={strategy.id} value={strategy.id}>
                              {strategy.badge} {strategy.name}
                            </option>
                          ))}
                        </Form.Select>
                        {selectedStrategy && (
                          <Button
                            variant="outline-primary"
                            className="rounded-3 shadow-sm px-3 d-flex align-items-center"
                            title="View Strategy Documentation"
                            onClick={() => setShowDocumentation(true)}
                          >
                            <Play size={16} />
                          </Button>
                        )}
                      </div>
                    </Form.Group>

                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold text-dark d-flex align-items-center">
                        <DollarSign size={16} className="me-2 text-primary" />
                        Trading Symbol
                      </Form.Label>
                      <Form.Select
                        value={selectedSymbol}
                        onChange={(e) => setSelectedSymbol(e.target.value)}
                        className="shadow-sm border-2 rounded-3 py-2"
                      >
                        {symbols.map((symbol) => (
                          <option key={symbol} value={symbol}>
                            {symbol}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Strategy Auto-fill Tip Banner */}
                {selectedStrategy && STRATEGY_PRESETS[selectedStrategy] && (
                  <div className="mt-3 p-3 rounded-4 border border-success border-opacity-25 bg-success bg-opacity-10 d-flex align-items-start gap-3">
                    <div className="p-2 bg-success rounded-3 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38 }}>
                      <Zap size={18} className="text-white" />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <span className="badge bg-success bg-opacity-75">⏱ TF: {STRATEGY_PRESETS[selectedStrategy].timeframe.toUpperCase()}</span>
                        <span className="badge bg-danger bg-opacity-75">🛡 SL: {STRATEGY_PRESETS[selectedStrategy].stopLoss}%</span>
                        <span className="badge bg-primary bg-opacity-75">🎯 Target: {STRATEGY_PRESETS[selectedStrategy].takeProfit}%</span>
                        {strategies.find(s => s.id === selectedStrategy)?.accuracy && (
                          <span className="badge bg-warning text-dark">✓ Accuracy: {strategies.find(s => s.id === selectedStrategy).accuracy}</span>
                        )}
                        {strategies.find(s => s.id === selectedStrategy)?.tradesPerDay && (
                          <span className="badge bg-info bg-opacity-75">📊 {strategies.find(s => s.id === selectedStrategy).tradesPerDay} trades/day</span>
                        )}
                      </div>
                      <p className="mb-0 small text-dark opacity-75">{STRATEGY_PRESETS[selectedStrategy].tip}</p>
                    </div>
                  </div>
                )}

                {/* Strategy Specific Parameters & Logic Optimizer Toggle */}
                {selectedStrategy && (
                  <div className="mt-4 mb-3">
                    {renderStrategyParameters(selectedStrategy)}
                  </div>
                )}

                <Row className="g-3 mt-1">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark d-flex align-items-center">
                        <Calendar size={16} className="me-2 text-primary" />
                        Start Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="shadow-sm border-2 rounded-3 py-2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark d-flex align-items-center">
                        <Calendar size={16} className="me-2 text-primary" />
                        End Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="shadow-sm border-2 rounded-3 py-2"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark d-flex align-items-center">
                        <Clock size={16} className="me-2 text-primary" />
                        Candle Timeframe
                      </Form.Label>
                      <Form.Select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                        className="shadow-sm border-2 rounded-3 py-2"
                      >
                        <optgroup label="Scalping">
                          <option value="1m">1 Minute</option>
                          <option value="3m">3 Minutes</option>
                          <option value="5m">5 Minutes</option>
                        </optgroup>
                        <optgroup label="Intraday">
                          <option value="15m">15 Minutes</option>
                          <option value="30m">30 Minutes</option>
                          <option value="1h">1 Hour</option>
                          <option value="4h">4 Hours</option>
                        </optgroup>
                        <optgroup label="Swing / Position">
                          <option value="1d">1 Day</option>
                          <option value="1w">1 Week</option>
                          <option value="1M">1 Month</option>
                        </optgroup>
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Selected: <strong>{
                          {
                            '1m': '1 Min', '3m': '3 Min', '5m': '5 Min', '15m': '15 Min',
                            '30m': '30 Min', '1h': '1 Hour', '4h': '4 Hour', '1d': 'Daily',
                            '1w': 'Weekly', '1M': 'Monthly'
                          }[timeframe] || timeframe
                        } candles</strong>
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Simplified Advanced Parameters & Risk Controls */}
                <div className="mt-4 p-3 border rounded bg-white mt-3">
                  <h6 className="fw-bold mb-3 pb-2 border-bottom">Risk Management & Parameters</h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Initial Capital ($)</Form.Label>
                        <Form.Control
                          type="number"
                          value={initialCapital}
                          onChange={(e) => setInitialCapital(parseFloat(e.target.value))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Stop Loss (%)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={advancedSettings.stopLoss}
                          onChange={(e) => setAdvancedSettings(prev => ({ ...prev, stopLoss: parseFloat(e.target.value) }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Take Profit (%)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={advancedSettings.takeProfit}
                          onChange={(e) => setAdvancedSettings(prev => ({ ...prev, takeProfit: parseFloat(e.target.value) }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Commission (%)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={advancedSettings.commission}
                          onChange={(e) => setAdvancedSettings(prev => ({ ...prev, commission: parseFloat(e.target.value) }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Leverage (x)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="100"
                          value={advancedSettings.leverage}
                          onChange={(e) => setAdvancedSettings(prev => ({ ...prev, leverage: parseInt(e.target.value) }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small fw-bold">Direction</Form.Label>
                        <Form.Select
                          value={advancedSettings.strategyType}
                          onChange={(e) => setAdvancedSettings(prev => ({ ...prev, strategyType: e.target.value }))}
                        >
                          <option value="both">Both (Long & Short)</option>
                          <option value="long">Long Only</option>
                          <option value="short">Short Only</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>





                {/* Step 3 & 4: Control Actions */}
                <div className="bg-light p-4 rounded-4 mt-4 border border-info border-opacity-25 shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-bold d-flex align-items-center">
                      <PlayCircle size={18} className="me-2 text-info" />
                      Execution Center
                    </h6>
                    <Badge bg="info" className="opacity-75">Analysis Console</Badge>
                  </div>

                  <div className="d-flex gap-3 flex-wrap">
                    <Button
                      variant="primary"
                      onClick={fetchHistoricalData}
                      disabled={isLoading || !selectedStrategy || !startDate || !endDate}
                      className="rounded-pill px-4 shadow py-2 d-flex align-items-center gap-2"
                    >
                      <Download size={18} />
                      Fetch Market Data
                    </Button>

                    <Button
                      variant="success"
                      onClick={runBacktest}
                      disabled={isLoading || !selectedStrategy || !historicalData.length || isOptimizationMode}
                      className="rounded-pill px-4 shadow py-2 d-flex align-items-center gap-2"
                    >
                      <Calculator size={18} />
                      Simulate Strategy
                    </Button>

                    {isOptimizationMode && (
                      <Button
                        variant="warning"
                        onClick={runOptimization}
                        disabled={isOptimizing || !selectedStrategy || !historicalData.length}
                        className="rounded-pill px-4 shadow py-2 d-flex align-items-center gap-2 text-dark fw-bold"
                      >
                        {isOptimizing ? <Activity size={18} className="animate-spin" /> : <Zap size={18} />}
                        {isOptimizing ? "Optimizing Logic..." : "Run Logic Optimizer"}
                      </Button>
                    )}

                    <Button
                      variant="outline-danger"
                      onClick={clearAllData}
                      className="rounded-pill px-4 py-2 d-flex align-items-center gap-2 ms-auto"
                    >
                      <RotateCcw size={16} />
                      Reset Everything
                    </Button>
                  </div>
                </div>





              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Quick Stats & Progress */}
            <Card className="border-0 shadow-sm mb-4 overflow-hidden" style={{ borderRadius: '15px' }}>
              <Card.Header className="bg-dark bg-gradient text-white py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <Activity size={18} className="me-2 text-warning" />
                  Simulation Status
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="simulation-status-list">
                  <div className="d-flex justify-content-between mb-4 pb-2 border-bottom">
                    <span className="text-muted"> Data Available:</span>
                    <strong className={historicalData.length > 0 ? "text-success" : "text-muted"}>
                      {historicalData.length ? `${historicalData.length} Candles` : "N/A"}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between mb-4 pb-2 border-bottom">
                    <span className="text-muted"><Calendar size={14} className="me-2" /> Period:</span>
                    <strong className="text-dark">
                      {startDate && endDate ? `${Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 3600 * 24))} Days` : "Select Range"}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted"><Zap size={14} className="me-2" /> Win Projection:</span>
                    <strong className="text-primary">Live Calculation</strong>
                  </div>
                </div>

                {isLoading && (
                  <div className="mt-5 p-3 bg-primary bg-opacity-10 rounded-3 border border-primary border-opacity-25 shadow-sm">
                    <div className="d-flex justify-content-between mb-2">
                      <small className="fw-bold text-primary">Engine Progress</small>
                      <small className="text-primary">{progress}%</small>
                    </div>
                    <ProgressBar now={progress} variant="primary" animated className="shadow-sm" style={{ height: '8px' }} />
                    <small className="text-muted d-block mt-2 text-center" style={{ fontSize: '11px' }}>Analyzing historical market patterns...</small>
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm bg-gradient" style={{ borderRadius: '15px', background: 'linear-gradient(135deg, #0d6efd 0%, #0dcaf0 100%)' }}>
              <Card.Body className="p-4 text-white">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <Shield size={18} className="me-2" />
                  Backtesting Tips
                </h6>
                <ul className="list-unstyled mb-0 small opacity-90">
                  <li className="mb-2"><i className="fas fa-check-circle me-1"></i> Use at least 6 months of data.</li>
                  <li className="mb-2"><i className="fas fa-check-circle me-1"></i> Verify against 1h timeframe.</li>
                  <li><i className="fas fa-check-circle me-1"></i> Check Monday vs Friday trend.</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Trade Execution Visualizer (Chart) - Full Width for better visibility */}
        {historicalData && historicalData.length > 0 && (
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                  <h6 className="fw-bold mb-0 text-dark d-flex align-items-center">
                    <MonitorPlay size={20} className="me-2 text-primary" />
                    Strategy Visualizer: {selectedSymbol} ({timeframe})
                  </h6>
                  {backtestResults && (
                    <div className="d-flex gap-2">
                       <Badge bg="primary" className="bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">
                         {backtestResults.trades.length} Signals Generated
                       </Badge>
                       <Badge bg="info" className="bg-opacity-10 text-info border border-info border-opacity-25 px-3 py-2">
                         Win Rate: {backtestResults.winRate.toFixed(1)}%
                       </Badge>
                    </div>
                  )}
                </Card.Header>
                <Card.Body className="p-3">
                  <BacktestingChart 
                    data={historicalData}
                    trades={backtestResults ? backtestResults.trades : []}
                    strategyParams={advancedSettings.ema_crossover || advancedSettings.ma_crossover || {}}
                    height={500}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Optimization Results Table */}
        {optResults && optResults.length > 0 && (
          <Card className="border-0 shadow-sm mb-4 overflow-hidden" style={{ borderRadius: '15px' }}>
            <Card.Header className="bg-warning bg-opacity-25 d-flex justify-content-between align-items-center py-3 border-0">
              <h5 className="mb-0 fw-bold d-flex align-items-center text-dark">
                <Activity size={20} className="me-2 text-warning" />
                Logic Optimization Results (Top {optResults.length} Combinations)
              </h5>
              <Badge bg="dark" className="px-3 py-2">
                Sorted by Net Profit
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle custom-table">
                  <thead className="bg-light">
                    <tr>
                      <th className="text-center">#</th>
                      <th className="text-center">Fast EMA</th>
                      <th className="text-center">Slow EMA</th>
                      <th className="text-center">Total Trades</th>
                      <th className="text-center">Win Rate %</th>
                      <th className="text-center">Net Profit ($)</th>
                      <th className="text-center">ROI %</th>
                      <th className="text-center">Max Drawdown</th>
                      <th className="text-center">Profit Factor</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optResults.slice(0, 20).map((res, idx) => (
                      <tr key={idx} className={idx === 0 ? "table-success bg-opacity-10" : ""}>
                        <td className="text-center fw-bold">{idx + 1}</td>
                        <td className="text-center">
                          <Badge bg="primary" pill className="px-3">{res.fastEMA}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="secondary" pill className="px-3">{res.slowEMA}</Badge>
                        </td>
                        <td className="text-center font-monospace">{res.totalTrades}</td>
                        <td className="text-center">
                          <span className={`fw-bold ${res.winRate > 50 ? "text-success" : "text-danger"}`}>
                            {res.winRate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="text-center fw-bold">
                          <span className={res.netProfit >= 0 ? "text-success" : "text-danger"}>
                            ${res.netProfit.toFixed(2)}
                          </span>
                        </td>
                        <td className="text-center">
                          <Badge bg={res.totalReturn >= 0 ? "success" : "danger"} className="rounded-pill">
                            {res.totalReturn >= 0 ? "+" : ""}{res.totalReturn.toFixed(2)}%
                          </Badge>
                        </td>
                        <td className="text-center text-danger">
                          -{res.maxDrawdown.toFixed(2)}%
                        </td>
                        <td className="text-center font-monospace">
                          {res.profitFactor.toFixed(2)}
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="rounded-pill px-3"
                            onClick={() => applyOptResult(res)}
                          >
                            Apply Settings
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {optResults.length > 20 && (
                <div className="p-3 text-center bg-light border-top text-muted small">
                  Showing top 20 of {optResults.length} parameter combinations. All results sorted by Performance Score.
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Results Section - Rendered Externally */}
        <BacktestResultsDisplay
          backtestResults={backtestResults}
          patternAnalysis={patternAnalysis}
          historicalDataLength={historicalData.length}
          tradeReport={generateTradeReport()}
        />

        {/* Strategy Documentation Modal */}
        <StrategyDocumentationModal
          strategyId={selectedStrategy}
          showDocumentation={showDocumentation}
          setShowDocumentation={setShowDocumentation}
        />
      </div>

      <style jsx>{`
        .backtesting-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px 15px;
          background: #fcfdfe;
          min-height: 100vh;
        }

        .transition-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .simulation-status-list strong {

        .simulation-status-list strong {
          letter-spacing: 0.5px;
        }

        .custom-table thead th {
           padding: 15px;
           font-weight: 500;
           font-size: 0.9rem;
        }

        .card {
           transition: box-shadow 0.3s ease;
        }

        .bg-gradient {
           background-size: 150% 150%;
        }

        .rounded-4 {
           border-radius: 1.25rem !important;
        }

        .rounded-pill {
           border-radius: 50rem !important;
        }

        .table-responsive::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .table-responsive::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 4px;
        }

        .table-responsive::-webkit-scrollbar-track {
          background: #f7fafc;
        }

        .sticky-top {
           z-index: 1020;
           background: white;
           box-shadow: 0 1px 0px rgba(0,0,0,0.05);
        }

        .btn-primary {
           background: linear-gradient(135deg, #0d6efd 0%, #0043a8 100%);
           border: none;
        }

        .btn-success {
           background: linear-gradient(135deg, #198754 0%, #0c5c38 100%);
           border: none;
        }

        .btn:disabled {
           opacity: 0.5;
           cursor: not-allowed;
           transform: none !important;
        }
      `}</style>
    </Content>
  );
};

export default Backtesting;
